import { InputBaseProps } from '@mui/material/InputBase';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { t } from 'i18next';
import { ReactNode } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldPathValue,
  FieldValues,
} from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';

interface FormFieldProps<TFieldValues extends FieldValues = FieldValues> {
  clearable?: boolean;
  control: Control<TFieldValues>;
  defaultValue?: Nullable<CompanyBasic>;
  disablePortal?: boolean;
  disabled?: boolean;
  errors: FieldErrors<TFieldValues>;
  hideLabel?: boolean;
  inputProps?: InputBaseProps['inputProps'];
  isRequired?: boolean;
  label?: string;
  name: string;
  noOptionsText?: string | ReactNode;
  sx?: SxProps<Theme>;
}

const CompanySelectorFormField = <TFieldValues extends FieldValues = FieldValues>({
  clearable = false,
  control,
  defaultValue = null,
  disablePortal,
  disabled = false,
  errors,
  hideLabel = false,
  inputProps,
  isRequired = false,
  label = 'Company',
  name,
  noOptionsText = t('form_fields.no_options'),
  sx,
}: FormFieldProps<TFieldValues>) => {
  const { userStore } = useStores();
  const { getAllUserAvailableCompaniesTypeahead } = useCompany();

  return userStore.userCompanies.length > 1 ? (
    <Controller
      name={name as FieldPath<TFieldValues>}
      control={control}
      defaultValue={defaultValue as FieldPathValue<TFieldValues, FieldPath<TFieldValues>>}
      render={() => (
        <AutocompleteAsyncFormField
          asyncCallback={getAllUserAvailableCompaniesTypeahead}
          clearable={clearable}
          control={control}
          defaultSuggestions={[]}
          disablePortal={disablePortal}
          disabled={disabled}
          errors={errors}
          getLabel={(company: CompanyBasic) => company.legalName}
          getValue={(company: CompanyBasic) => company.id}
          hideLabel={hideLabel}
          inputProps={{ 'data-test-id': 'company-selector-form-field', ...inputProps }}
          isRequired={isRequired}
          label={label}
          multiple={false}
          name={name}
          noOptionsText={noOptionsText}
          sx={sx}
        />
      )}
    />
  ) : null;
};

export { CompanySelectorFormField };
